import React, { useEffect, useRef, useState } from 'react';
import {
  findAirportsWithinDriveRadius,
  findAirportsWithinRadiusDynamic,
} from '@wander/shared/utils/transportPrefetch.js';
import supabase from '../../lib/supabase';

/**
 * "Drive further for more routes" disclosure — progressively enhanced.
 *
 * Renders an instant curated result from MULTI_AIRPORT_CITIES (sync), then
 * upgrades to the 4,500-row dynamic airports_within_radius RPC result once
 * it returns. Users of Tokyo/Newcastle/etc. now see airport alternatives
 * they never saw under the pure curated loop.
 *
 * Self-contained — owns its async state so the parent render doesn't need to
 * thread it through. Graceful fallback on RPC failure (keeps curated result).
 *
 * @param {Object} props
 * @param {string} props.from - Origin city name (departure) OR arrival city (arrival)
 * @param {string} [props.currentCode] - Currently-selected IATA; filtered out
 * @param {Array<{code:string}>} [props.excludeAlternatives] - IATAs to hide
 * @param {(code:string) => void} props.onSelect - Fires when user picks an airport
 * @param {number} [props.maxMiles=300]
 * @param {'departure'|'arrival'} [props.direction='departure'] - Which side of the
 *   journey. Drives the summary/body copy: departure "Depart from a larger hub";
 *   arrival "Fly into a larger hub".
 */
export default function DriveFurtherPicker({
  from,
  currentCode,
  excludeAlternatives = [],
  onSelect,
  maxMiles = 300,
  direction = 'departure',
}) {
  // Start with the curated-list result so the disclosure appears instantly.
  // Async effect below merges in the dynamic result.
  const [extras, setExtras] = useState(() =>
    from ? findAirportsWithinDriveRadius(from, { maxMiles }) : []
  );
  const fetchedForRef = useRef(null);

  useEffect(() => {
    if (!from || fetchedForRef.current === from) return;
    fetchedForRef.current = from;
    let alive = true;
    findAirportsWithinRadiusDynamic(from, { maxMiles, supabase })
      .then((result) => {
        if (!alive || !Array.isArray(result) || result.length === 0) return;
        // Always prefer the dynamic result when it has MORE options than curated —
        // it's backed by the full OurAirports master list so it can only widen
        // the available set, never shrink it meaningfully.
        setExtras((prev) => (result.length > prev.length ? result : prev));
      })
      .catch(() => {
        /* swallow — curated fallback already rendered */
      });
    return () => {
      alive = false;
    };
  }, [from, maxMiles]);

  if (!from) return null;
  const filtered = extras.filter(
    (x) => x.code !== currentCode && !excludeAlternatives.some((a) => a.code === x.code)
  );
  if (!filtered.length) return null;

  const isArrival = direction === 'arrival';
  const cityShort = from.split(',')[0].trim();
  return (
    <details className="mb-3 text-xs">
      <summary className="cursor-pointer text-teal-700 hover:text-teal-800 select-none">
        🚗 {isArrival ? 'Fly into a different hub' : 'Drive further for more routes'} ({filtered.length})
      </summary>
      <div className="mt-2 pl-4 space-y-1">
        <p className="text-[11px] text-gray-500">
          {isArrival
            ? `Fly into a larger hub instead — we'll assume you drive to ${cityShort} from there.`
            : `Depart from a larger hub instead — we'll assume you drive there from ${cityShort}.`}
        </p>
        {filtered.slice(0, 10).map((alt) => (
          <button
            key={alt.code}
            type="button"
            onClick={() => onSelect(alt.code)}
            className="block w-full text-left px-2 py-1 rounded hover:bg-teal-50 text-gray-700"
          >
            <span className="font-mono font-bold text-teal-700">{alt.code}</span>{' '}
            <span className="text-gray-600">{alt.name}</span>{' '}
            <span className="text-gray-400">
              · {typeof alt.estDriveHours === 'number' ? `~${alt.estDriveHours.toFixed(1)}h drive ` : ''}
              ({Math.round(alt.crowMiles)}mi){alt.isLargeHub ? ' · hub' : ''}
            </span>
          </button>
        ))}
      </div>
    </details>
  );
}
